import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SectionHeading } from './_shared';
import PropertyCardCompact from './PropertyCardCompact';
import { useGeolocation } from '../../../hooks/useGeolocation';
import { searchProperties, toFrontendProperty, type SearchProperty } from '../../../services/ragSearchService';
import { GooeyLoader } from '../../../components/ui/gooey-loader';

type FrontendProperty = ReturnType<typeof toFrontendProperty>;

// "Featured properties" — real, newest listings near the visitor (falls back to
// the default city when geolocation isn't available). Each card badges its own
// For-Sale / For-Rent state, matching the 3a grid.
export default function FeaturedProperties() {
  const { t, i18n } = useTranslation(['home']);
  const isRTL = i18n.dir() === 'rtl';
  const { nearestCity, loading: geoLoading } = useGeolocation();

  const [items, setItems] = useState<FrontendProperty[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (geoLoading) return;
    let cancelled = false;
    setLoading(true);
    setError(false);

    searchProperties({
      city: nearestCity?.nameEn,
      limit: 6,
      sort: 'newest',
      lang: i18n.language === 'ar' ? 'ar' : 'en',
    })
      .then((res) => {
        if (cancelled) return;
        if (res.success && res.data) {
          setItems(res.data.properties.slice(0, 6).map((p: SearchProperty) => toFrontendProperty(p)));
        } else {
          setItems([]);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setItems([]);
          setError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [nearestCity?.nameEn, i18n.language, geoLoading]);

  return (
    <section className="border-t border-[var(--pv-border-subtle)] bg-white py-14 sm:py-20">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <SectionHeading
          title={t('home.v2.featured.title')}
          action={{ label: t('home.v2.featured.browseAll'), to: '/search' }}
          isRTL={isRTL}
        />

        {loading ? (
          <div className="flex flex-col items-center justify-center gap-5 py-16" role="status" aria-live="polite">
            {/* Soft lavender so the loading glow is light + easy on the eyes. */}
            <GooeyLoader primaryColor="#7AC3B7" secondaryColor="#B7DDD7" borderColor="#DDEEEB" />
            <p className="text-sm text-ink-muted">
              {t('home.location.loading', { defaultValue: isRTL ? 'جاري تحميل العقارات…' : 'Loading properties…' })}
            </p>
          </div>
        ) : items.length > 0 ? (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((property, i) => (
              <PropertyCardCompact key={property.id} property={property} index={i} />
            ))}
          </div>
        ) : (
          <div className="mx-auto max-w-md rounded-2xl border border-[var(--pv-border-soft)] bg-white p-10 text-center" role="status">
            <i
              className={`mb-3 text-4xl ${error ? 'ri-error-warning-line text-red-400' : 'ri-home-heart-line text-mq-primary-light'}`}
              aria-hidden="true"
            />
            <p className="text-ink-muted">{t('home.featured.empty')}</p>
          </div>
        )}
      </div>
    </section>
  );
}
